import { mockApiRequest, MockApiError, type MockApiScenario } from "./mockApi";
import type { PlatformAppId } from "../config/environment";

export interface MockRecord {
  id: string;
}
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
export interface ListOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  scenario?: MockApiScenario;
}

export function createMockRepository<T extends MockRecord>(
  app: PlatformAppId,
  resource: string,
  seed: readonly T[],
) {
  const key = `chatoner-academy:mock:${resource}:v1`;
  let memory = seed.map(item => ({ ...item }));
  const read = () => {
    if (typeof window === "undefined") return memory;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || "null") as T[] | null;
      return Array.isArray(saved) ? saved : memory;
    } catch {
      return memory;
    }
  };
  const write = (items: T[]) => {
    memory = items;
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(key, JSON.stringify(items));
      } catch {
        // Memory remains the fallback when browser storage is unavailable or full.
      }
    }
  };
  return {
    async list(options: ListOptions = {}) {
      const page = Math.max(1, options.page || 1),
        pageSize = Math.max(1, options.pageSize || 25),
        query = options.search?.trim().toLowerCase();
      const all = read().filter(item => !query || JSON.stringify(item).toLowerCase().includes(query));
      const result = {
        items: all.slice((page - 1) * pageSize, page * pageSize),
        total: all.length,
        page,
        pageSize,
      };
      return (
        await mockApiRequest({
          app,
          data: result,
          emptyData: { ...result, items: [], total: 0 },
          scenario: options.scenario,
        })
      ).data;
    },
    async get(id: string, scenario?: MockApiScenario) {
      const item = read().find(record => record.id === id);
      if (!item) throw new MockApiError(`${resource} record was not found.`, 404, "NOT_FOUND");
      return (await mockApiRequest({ app, data: item, emptyData: item, scenario })).data;
    },
    async create(input: T, scenario?: MockApiScenario) {
      const items = read();
      if (items.some(item => item.id === input.id))
        throw new MockApiError(`${resource} id already exists.`, 409, "CONFLICT");
      const next = [...items, input];
      const result = (await mockApiRequest({ app, data: input, emptyData: input, scenario })).data;
      write(next);
      return result;
    },
    async update(id: string, changes: Partial<T>, scenario?: MockApiScenario) {
      const items = read(),
        index = items.findIndex(item => item.id === id);
      if (index < 0) throw new MockApiError(`${resource} record was not found.`, 404, "NOT_FOUND");
      const updated = { ...items[index], ...changes, id } as T;
      const next = [...items];
      next[index] = updated;
      const result = (await mockApiRequest({ app, data: updated, emptyData: updated, scenario })).data;
      write(next);
      return result;
    },
    async remove(id: string, scenario?: MockApiScenario) {
      const items = read();
      if (!items.some(item => item.id === id))
        throw new MockApiError(`${resource} record was not found.`, 404, "NOT_FOUND");
      const result = (await mockApiRequest({ app, data: { id }, emptyData: { id }, scenario })).data;
      write(items.filter(item => item.id !== id));
      return result;
    },
    reset() {
      write(seed.map(item => ({ ...item })));
    },
  };
}
